// Étapes du funnel que seul le navigateur voit.
// Formulaire d'inscription et écrans d'upgrade.
// Aucun identifiant direct, seulement des listes fermées.
// Architecture : docs/FUNNEL_EVENTS_ARCHITECTURE.md.

import { reportApiRefusal } from "@/lib/errorReporter"
import { api } from "@/services/api"
import { publicPost } from "@/services/public-fetch"

/** Écran d'upgrade d'origine : la limite de plan. */
export type UpgradeContext = "plan_limit"

/** Plan proposé par un écran d'upgrade (code de plan). */
export type UpgradeTargetPlan = "solo" | "equipe" | "enterprise"

const UPGRADE_TARGET_PLANS: ReadonlyArray<string> = ["solo", "equipe", "enterprise"]

/** Étape envoyée par un organisme connecté. */
export type FunnelStep =
  | {
      event: "upgrade_prompt_shown" | "upgrade_prompt_clicked" | "upgrade_prompt_dismissed"
      context: UpgradeContext
      target_plan: UpgradeTargetPlan
    }
  | { event: "upgrade_requested"; target_plan: UpgradeTargetPlan }

/**
 * Code de plan proposable par un écran d'upgrade, sinon `undefined`.
 *
 * @example
 * asUpgradeTargetPlan("equipe") // "equipe"
 * asUpgradeTargetPlan("free") // undefined
 */
export function asUpgradeTargetPlan(plan: unknown): UpgradeTargetPlan | undefined {
  return typeof plan === "string" && UPGRADE_TARGET_PLANS.includes(plan) ? (plan as UpgradeTargetPlan) : undefined
}

/**
 * Enregistre une étape du funnel.
 * Un échec n'ouvre aucun écran d'erreur.
 * Il part au journal des refus d'API.
 *
 * @example
 * recordFunnelStep({ event: "upgrade_requested", target_plan: "equipe" })
 */
export function recordFunnelStep(step: FunnelStep): void {
  // comply-ignore: async-await-only — la mesure ne bloque pas l'appelant.
  // comply-ignore: api-first — le corps est typé par FunnelStep.
  // comply-ignore: api-route-version-prefix — le client `api` préfixe déjà /api/v1.
  api.post("/funnel-events", step).catch(reportApiRefusal)
}

/**
 * Enregistre une demande de passage au plan `plan`.
 * Elle part de l'écran de limite ou de l'onglet Abonnement.
 * Un plan non proposé n'est pas enregistré.
 *
 * @example
 * recordUpgradeRequested("equipe") // POST /api/v1/funnel-events { event: "upgrade_requested", target_plan: "equipe" }
 */
export function recordUpgradeRequested(plan: string): void {
  const target_plan = asUpgradeTargetPlan(plan)
  if (target_plan) recordFunnelStep({ event: "upgrade_requested", target_plan })
}

/**
 * Enregistre l'affichage du formulaire d'inscription (sans compte), avec le
 * seul type d'écran.
 *
 * @example
 * recordSignupFormViewed() // POST /api/v1/public/funnel-events { device: "desktop" }
 */
export function recordSignupFormViewed(): void {
  const device = window.matchMedia?.("(max-width: 767px)").matches ? "mobile" : "desktop"
  // Réponse 204 sans corps : la lecture du JSON échoue, sans conséquence.
  publicPost("/api/v1/public/funnel-events", { event: "signup_form_viewed", device }).catch(() => undefined)
}
